import { usePreferences, useUpdatePreferences } from '@/api/preferences';
import { ModelSelect } from '@/components/ai/ModelSelect';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
/**
 * Settings (app-wide, per-device). First section: text size — sliders that scale
 * a few key reading surfaces on phone-sized screens so packing on a phone is
 * easier to read, without changing the desktop layout. Preferences persist in
 * localStorage (see stores/fontSettings).
 */
import { DEPLOY_TIME_EASTERN, GIT_COMMIT } from '@/lib/buildInfo';
import { forceReloadApp, useIsStandalone } from '@/lib/pwa';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useAiModel } from '@/stores/aiModel';
import {
  FONT_BASE_PX,
  FONT_SCALE_MAX,
  FONT_SCALE_MIN,
  FONT_SCALE_STEP,
  type FontScaleKey,
  useFontSettings,
} from '@/stores/fontSettings';
import { createFileRoute } from '@tanstack/react-router';
import { USER_PREFERENCE_DEFAULTS } from '@ultimate-checklist/shared';
import { CloudCog, RotateCcw, RotateCw, Smartphone, Sparkles } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

const TEXT_ROWS: Array<{ key: FontScaleKey; label: string; sample: string }> = [
  { key: 'listTitle', label: 'List title', sample: 'Beach Day' },
  { key: 'listItem', label: 'List items', sample: 'Sunscreen' },
  { key: 'appText', label: 'Everything else', sample: 'All other text across the app' },
];

function SettingsPage() {
  useDocumentTitle('Settings');
  const scales = useFontSettings((state) => state.scales);
  const setScale = useFontSettings((state) => state.setScale);
  const reset = useFontSettings((state) => state.reset);
  const aiModel = useAiModel((state) => state.model);
  const setAiModel = useAiModel((state) => state.setModel);
  const isDefault = TEXT_ROWS.every((row) => scales[row.key] === 1);
  const isStandalone = useIsStandalone();
  const [reloading, setReloading] = useState(false);
  const preferences = usePreferences();
  const updatePreferences = useUpdatePreferences();
  const prefs = preferences.data ?? USER_PREFERENCE_DEFAULTS;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-[var(--muted-foreground)]">
          Account settings follow you to every device. Text size and AI model stay on this device.
        </p>
      </header>

      <section className="flex flex-col gap-4 rounded-xl border border-[var(--border)] p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Your account</h2>
          <p className="flex items-center gap-1.5 text-sm text-[var(--muted-foreground)]">
            <CloudCog className="h-4 w-4 shrink-0" />
            Saved to your account, so they apply wherever you sign in.
          </p>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <Label htmlFor="pref-highlight-applied" className="text-sm font-medium">
              Highlight changes after applying
            </Label>
            <p className="text-sm text-[var(--muted-foreground)]">
              After you apply other people’s changes to a trip, mark the touched rows until you
              clear them. Off means the board just updates.
            </p>
          </div>
          <Switch
            id="pref-highlight-applied"
            checked={prefs.highlightAppliedChanges}
            disabled={preferences.isPending}
            onCheckedChange={(checked) =>
              updatePreferences.mutate({ highlightAppliedChanges: checked })
            }
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="pref-live-collaboration" className="text-sm font-medium">
              Enable live collaboration
            </Label>
            <p className="text-sm text-[var(--muted-foreground)]">
              Adds a “Live collaboration” switch to each trip’s Manage panel. On a live trip you see
              other people’s edits the moment they happen, and who has an item open, instead of
              holding their changes until you tap Apply.
            </p>
          </div>
          <Switch
            id="pref-live-collaboration"
            checked={prefs.liveCollaboration}
            disabled={preferences.isPending}
            onCheckedChange={(checked) => updatePreferences.mutate({ liveCollaboration: checked })}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-[var(--border)] p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">Text size</h2>
          <p className="flex items-center gap-1.5 text-sm text-[var(--muted-foreground)]">
            <Smartphone className="h-4 w-4 shrink-0" />
            Applies on phone-sized screens so lists are easier to read while packing. Your desktop
            view stays the same.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {TEXT_ROWS.map((row) => {
            const scale = scales[row.key];
            const sliderId = `font-${row.key}`;
            return (
              <div key={row.key} className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={sliderId} className="text-sm font-medium">
                    {row.label}
                  </Label>
                  <span className="text-sm tabular-nums text-[var(--muted-foreground)]">
                    {Math.round(scale * 100)}%
                  </span>
                </div>
                {/* Live preview at the chosen size (shown at every screen size so you
                    can dial it in from a desktop too). */}
                <div className="flex min-h-12 items-center rounded-lg border border-dashed border-[var(--border)] bg-[var(--muted)] px-3 py-2">
                  <span
                    className="font-medium text-black dark:text-white"
                    style={{ fontSize: `${FONT_BASE_PX[row.key] * scale}px` }}
                  >
                    {row.sample}
                  </span>
                </div>
                <input
                  id={sliderId}
                  type="range"
                  min={FONT_SCALE_MIN}
                  max={FONT_SCALE_MAX}
                  step={FONT_SCALE_STEP}
                  value={scale}
                  onChange={(event) => setScale(row.key, Number(event.target.value))}
                  className="w-full accent-[var(--primary)]"
                  aria-label={`${row.label} text size`}
                />
              </div>
            );
          })}
        </div>

        <div>
          <Button type="button" variant="outline" size="sm" onClick={reset} disabled={isDefault}>
            <RotateCcw />
            Reset to default
          </Button>
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-xl border border-[var(--border)] p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">AI model</h2>
          <p className="flex items-center gap-1.5 text-sm text-[var(--muted-foreground)]">
            <Sparkles className="h-4 w-4 shrink-0" />
            Powers Quick add when it turns what you say into items. “Fast” models reply quicker;
            “best” models read your request more carefully.
          </p>
        </div>
        <ModelSelect id="settings-ai-model" value={aiModel} onChange={setAiModel} />
      </section>

      {/* Always visible (stale bundles bite in plain browser tabs too, not just
          the installed app): force reload + the identity of the running build. */}
      <section className="flex flex-col gap-4 rounded-xl border border-[var(--border)] p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">App</h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            {isStandalone
              ? 'Running as an installed app. If something looks out of date, force a reload to fetch the latest version.'
              : 'If something looks out of date, force a reload to fetch the latest version.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={reloading}
            onClick={() => {
              setReloading(true);
              void forceReloadApp();
            }}
          >
            <RotateCw />
            {reloading ? 'Reloading…' : 'Force reload'}
          </Button>
          <p className="text-xs text-[var(--muted-foreground)]">
            Build <code className="font-mono">{GIT_COMMIT}</code>
            {DEPLOY_TIME_EASTERN ? <> · deployed {DEPLOY_TIME_EASTERN}</> : null}
          </p>
        </div>
      </section>
    </div>
  );
}
